import {LicenceType, OrganisationType} from '@cypro/admin-engine/database/schema'
import * as z from 'zod'

const LicenceLimitsFormSchema = z.object({
  // null means uncapped (unlimited concurrent users)
  concurrentUsers: z.int().min(1).nullable(),
})

export const IssueLicenceFormSchema = z
  .discriminatedUnion('type', [
    z.object({
      type: z.literal(LicenceType.enum.limited),
      limits: LicenceLimitsFormSchema,
      start: z.iso.date().nonoptional(),
      exp: z.iso.date().nonoptional(),
    }),
    z.object({
      type: z.literal(LicenceType.enum.lifetime),
      limits: LicenceLimitsFormSchema,
    }),
  ])
  .superRefine((values, ctx) => {
    const {type} = values

    if (type === LicenceType.enum.limited) {
      const {start, exp} = values
      if (start && exp && new Date(start) >= new Date(exp)) {
        ctx.addIssue({
          path: ['start'],
          code: 'custom',
          continue: true,
          message: 'start_date_cannot_be_before_end_date',
        })
      }
    }
  })
export type IssueLicenceFormSchema = z.infer<typeof IssueLicenceFormSchema>

export const OrganisationFormSchema = z
  .object({
    id: z.uuidv7(), // we need the id generated on the frontend because it is used to mint the licence for the organisation, after creation, but zero doesn't return the created record
    name: z.string().trim().nonempty(),
    type: OrganisationType.nonoptional(),
    street: z.string(),
    country: z.string().trim().nonempty(),
    isDemo: z.boolean(),

    licence: IssueLicenceFormSchema,

    admin: z.object({
      firstName: z.string().trim().nonempty(),
      lastName: z.string().trim().nonempty(),
      email: z.email().trim().nonempty(),
    }),

    addMore: z.boolean(),
  })
export type OrganisationFormSchema = z.infer<typeof OrganisationFormSchema>

/**
 * The issue-licence dialog edits only the licence, but nests it under `licence`
 * so it can render the same `LicenceFormFields` group as the organisation form.
 */
export const IssueLicenceDialogFormSchema = z.object({
  licence: IssueLicenceFormSchema,
})
export type IssueLicenceDialogFormSchema = z.infer<typeof IssueLicenceDialogFormSchema>

export const OrganisationsSearch = z.object({
  filters: z
    .object({
      search: z.string().trim().optional(),
    })
    .optional(),
})
export type OrganisationsSearch = z.infer<typeof OrganisationsSearch>
